import React, { useState, useEffect } from "react";
import app from "../../../backend/firebaseConfig";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { getFirestore, doc, setDoc, deleteDoc, getDocs, getDoc, runTransaction, deleteField, collection, writeBatch } from "firebase/firestore";
import {
  getAuth,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  fetchSignInMethodsForEmail,
  signOut,
  deleteUser,
} from "firebase/auth";
import { query, where, collection as firestoreCollection } from "firebase/firestore";

import Snackbar from "../../../components/UI/atoms/snackbar/Snackbar";
import CustomAlert from "../../../components/UI/molecules/alert/CustomAlert";


// Import Assets
import stars from "../../../assets/stars.png";
import back from "../../../assets/back.png";
import logo from "../../../assets/logo.png";

// Imports MUI Components
import { 
  Box, 
  Button, 
  Stack, 
  Typography, 
  Grid, 
  CircularProgress,
  Dialog,
  DialogContent,
  FormControl,
  MenuItem,
  Select as Selected,
  Divider
} from "@mui/material";
import { Email, CheckCircle, Close } from "@mui/icons-material";

// Import Atoms
import TextFieldComp from "../../../components/UI/atoms/text-fields/TextField";
import Icon from "../../../components/UI/atoms/icon/Icon";
import Text from "../../../components/UI/atoms/text/Text";

// Function to sync new dentist with existing closed dates
const syncDentistWithClosedDates = async (db, dentistUid) => {
  try {
    // Fetch existing closed dates
    const closedDaysRef = doc(db, "closedDays", "closedDates");
    const closedDaysSnap = await getDoc(closedDaysRef);
    
    if (!closedDaysSnap.exists()) return;
    
    const closedDates = closedDaysSnap.data().dates || [];
    if (closedDates.length === 0) return;
    
    // Generate all time slots for the day (9 AM - 6 PM, 30-minute intervals)
    const allTimeSlots = [];
    for (let hour = 9; hour < 18; hour++) {
      for (let minutes = 0; minutes < 60; minutes += 30) {
        const startMinutes = hour * 60 + minutes;
        const endMinutes = startMinutes + 30;
        
        // Skip if the slot would go beyond 6 PM
        if (endMinutes > 18 * 60) {
          continue;
        }
        
        const startHours = Math.floor(startMinutes / 60);
        const startMins = startMinutes % 60;
        const startAmpm = startHours >= 12 ? 'PM' : 'AM';
        const startDisplayHours = startHours % 12 || 12;
        const startTimeStr = `${startDisplayHours}:${startMins.toString().padStart(2, '0')} ${startAmpm}`;
        
        const endHours = Math.floor(endMinutes / 60);
        const endMins = endMinutes % 60;
        const endAmpm = endHours >= 12 ? 'PM' : 'AM';
        const endDisplayHours = endHours % 12 || 12;
        const endTimeStr = `${endDisplayHours}:${endMins.toString().padStart(2, '0')} ${endAmpm}`;
        
        allTimeSlots.push(`${startTimeStr} - ${endTimeStr}`);
      }
    }
    
    // For each closed date, add this dentist's unavailable slots
    for (const dateStr of closedDates) {
      const timeSlotsRef = doc(db, "unavailableTimeSlots", dateStr);
      await setDoc(timeSlotsRef, {
        [dentistUid]: allTimeSlots
      }, { merge: true });
    }
    
    console.log(`Synced dentist ${dentistUid} with ${closedDates.length} closed dates`);
  } catch (error) {
    console.error('Error syncing dentist with closed dates:', error);
  }
};


// Helper function to capitalize first letter of each word
const capitalizeName = (name) => {
  if (!name) return "";
  return name
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const SignUpPatient = () => {
  // state variables
  const [email, setEmail] = useState("");
  const [userPassword, setUserPassword] = useState("");
  const [userConfirmPassword, setUserConfirmPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [middleName, setMiddleName] = useState("");
  const [lastName, setLastName] = useState("");
  const [address, setAddress] = useState("");
  const [contactNumber, setContactNumber] = useState("");
  const [civilStatus, setCivilStatus] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [age, setAge] = useState("");
  const [gender, setGender] = useState("");
  const [showVerifyModal, setShowVerifyModal] = useState(false);
    const [isVerified, setIsVerified] = useState(false);
  const [showCustomAlert, setShowCustomAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isMobileOnly, setIsMobileOnly] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendError, setResendError] = useState("");
  const [isCheckingVerification, setIsCheckingVerification] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState("");
  const [redirectCountdown, setRedirectCountdown] = useState(3);

  // Validation error states
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [isCheckingMobile, setIsCheckingMobile] = useState(false);
  
  // Reactivation modal state
  const [showReactivationModal, setShowReactivationModal] = useState(false);
  const [reactivationModalData, setReactivationModalData] = useState(null);
  const [isReactivating, setIsReactivating] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const auth = getAuth(app);
  const db = getFirestore(app);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const pendingKey = location.state?.pendingKey || null;
  const pendingType = location.state?.pendingType || null;

  // Auto-calculate age from birthdate
  useEffect(() => {
    if (birthDate) {
      const birth = new Date(birthDate);
      const today = new Date();
      let ageNow = today.getFullYear() - birth.getFullYear();
      const m = today.getMonth() - birth.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
        ageNow--;
      }
      setAge(ageNow >= 0 ? ageNow : "");
    } else {
      setAge("");
    }
  }, [birthDate]);

  // regex pattern for email and password validation
  const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const passwordPattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*]).+$/;

  // Validation function
  const validateField = (fieldName, value) => {
    const isPersonnel = pendingType && (pendingType === "DentistOwner" || pendingType === "AssociateDentist" || pendingType === "ClinicStaff");
    
    switch (fieldName) {
      case "email":
        // For personnel: email is required
        if (isPersonnel && !value) return "Email address is required";
        // For patients: email is optional, but if provided, must be valid
        if (value && !emailPattern.test(value))
          return "Please enter a valid email address";
        return "";
      case "userPassword":
        if (!value) return "Password is required";
        if (value.length < 8)
          return "Password must be at least 8 characters long";
        if (!passwordPattern.test(value))
          return "Password must contain an uppercase letter, a lowercase letter, a number, and a special character";
        return "";
      case "userConfirmPassword":
        if (!value) return "Confirm Password is required";
        if (value !== userPassword) return "Passwords do not match";
        return "";
      case "firstName":
        if (!value) return "First Name is required";
        return "";
      case "lastName":
        if (!value) return "Last Name is required";
        return "";
      case "address":
        if (!value) return "Address is required";
        return "";
      case "contactNumber":
        // For personnel: mobile is required
        if (isPersonnel && !value) return "Mobile number is required";
        // For everyone: if provided, must start with '09'
        if (value && !value.toString().startsWith('09'))
          return "Mobile number must start with '09'";
        // Mobile number must be exactly 11 digits
        if (value && value.toString().length !== 11)
          return "Mobile number must be 11 digits (e.g., 09XXXXXXXXX)";
        return "";
      case "birthDate":
        if (!value) return "Birthdate is required";
        const selectedDate = new Date(value);
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Reset time to compare only dates
        selectedDate.setHours(0, 0, 0, 0);
        
        if (selectedDate.getTime() === today.getTime())
          return "Birthdate cannot be today";
        if (selectedDate > today)
          return "Birthdate must not be in the future";
        return "";
      case "age":
        if (!value) return "Age is required";
        return "";
      case "gender":
        if (!value) return "Gender is required";
        return "";
      default:
        return "";
    }
  };

  // Handle Enter key to submit sign-up form
  const handleKeyDownSignUp = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      // Avoid submitting while already loading
      if (!isLoading) handleSubmit(e);
    }
  };

  // Check if email already exists in the system
  const checkEmailExists = async (emailValue) => {
    if (!emailValue || !emailPattern.test(emailValue)) {
      return false; // Don't check if email is empty or invalid
    }

    setIsCheckingEmail(true);
    try {
      // Check Firestore first (users collection) - exclude deleted accounts
      const emailQuery = query(
        firestoreCollection(db, "users"),
        where("email", "==", emailValue.trim()),
        where("accountDeleted", "==", false)
      );
      const emailSnapshot = await getDocs(emailQuery);
      
      if (!emailSnapshot.empty) {
        setErrors((prev) => ({ 
          ...prev, 
          email: "This email address is already in use by another account" 
        }));
        setIsCheckingEmail(false);
        return true;
      }

      // Also check authEmail field in Firestore - exclude deleted accounts
      const authEmailQuery = query(
        firestoreCollection(db, "users"),
        where("authEmail", "==", emailValue.trim()),
        where("accountDeleted", "==", false)
      );
      const authEmailSnapshot = await getDocs(authEmailQuery);
      
      if (!authEmailSnapshot.empty) {
        setErrors((prev) => ({ 
          ...prev, 
          email: "This email address is already in use by another account" 
        }));
        setIsCheckingEmail(false);
        return true;
      }

      // Determine authEmail based on email and mobile
      let authEmail = emailValue;
      
      // If no email but has mobile (mobile-only signup), we don't check Firebase Auth
      if (!emailValue && contactNumber) {
        setIsCheckingEmail(false);
        return false;
      }

      // Check if the auth email is already in use in Firebase Auth
      try {
        const methods = await fetchSignInMethodsForEmail(auth, authEmail);
        if (methods.length > 0) {
          setErrors((prev) => ({ 
            ...prev, 
            email: "This email address is already in use by another account" 
          }));
          setIsCheckingEmail(false);
          return true;
        }
      } catch (emailCheckError) {
        // If quota is exceeded, we've already checked Firestore, so we can proceed
        // Firebase will still prevent duplicate emails during account creation
        if (emailCheckError.code === "auth/quota-exceeded") {
          console.warn("Email lookup quota exceeded, but Firestore check passed");
          setIsCheckingEmail(false);
          return false;
        }
        // For other errors, re-throw
        throw emailCheckError;
      }

      // Clear email error if email is available
      setErrors((prev) => ({ ...prev, email: prev.email?.includes("already in use") ? "" : prev.email }));
      setIsCheckingEmail(false);
      return false;
    } catch (error) {
      console.error("Error checking email:", error);
      setIsCheckingEmail(false);
      return false;
    }
  };

  // Check if mobile number already exists in the system
  const checkMobileExists = async (mobileValue) => {
    if (!mobileValue || mobileValue.toString().length !== 11 || !mobileValue.toString().startsWith('09')) {
      return false; // Don't check if mobile is empty or invalid
    }

    setIsCheckingMobile(true);
    try {
      const mobileQuery = query(
        firestoreCollection(db, "users"),
        where("mobile", "==", mobileValue),
        where("accountDeleted", "==", false)
      );
      const mobileSnapshot = await getDocs(mobileQuery);
      
      if (!mobileSnapshot.empty) {
        setErrors((prev) => ({ 
          ...prev, 
          contactNumber: "This mobile number is already in use by another account" 
        }));
        setIsCheckingMobile(false);
        return true;
      }

      // Clear mobile error if mobile is available
      setErrors((prev) => ({ ...prev, contactNumber: prev.contactNumber?.includes("already in use") ? "" : prev.contactNumber }));
      setIsCheckingMobile(false);
      return false;
    } catch (error) {
      console.error("Error checking mobile:", error);
      setIsCheckingMobile(false);
      return false;
    }
  };

  // Handle field blur (when user leaves a field)
  // This validation works for all user types: Patients, DentistOwner, AssociateDentist, and ClinicStaff
  const handleBlur = async (fieldName) => {
    setTouched((prev) => ({ ...prev, [fieldName]: true }));
    const value = {
      email,
      userPassword,
      userConfirmPassword,
      firstName,
      lastName,
      address,
      contactNumber,
      birthDate,
      age,
      gender,
    }[fieldName];
    let error = validateField(fieldName, value);
    
    // For patients: check if both email and mobile are empty when either field is blurred
    const isPersonnel = pendingType && (pendingType === "DentistOwner" || pendingType === "AssociateDentist" || pendingType === "ClinicStaff");
    if (!isPersonnel && (fieldName === "email" || fieldName === "contactNumber")) {
      // If neither email nor mobile is provided, show the error message on the blurred field
      if (!email && !contactNumber) {
        // Don't override "already in use" errors
        const existingError = errors[fieldName];
        if (!existingError || !existingError.includes("already in use")) {
          error = "Please provide either an email address or a mobile number (or both)";
        }
      }
    }
    
    // Update errors for the blurred field
    setErrors((prev) => {
      const updated = { ...prev, [fieldName]: error };
      
      // For patients: if one of email/mobile has a value, clear the "either/or" error on both fields if present
      if (!isPersonnel && (fieldName === "email" || fieldName === "contactNumber")) {
        if (email || contactNumber) {
          const eitherOrMessage = "Please provide either an email address or a mobile number (or both)";
          // Check previous state for the "either/or" message, not the updated state (which we just modified)
          if (prev.email === eitherOrMessage && !prev.email?.includes("already in use")) {
            updated.email = "";
          }
          if (prev.contactNumber === eitherOrMessage && !prev.contactNumber?.includes("already in use")) {
            updated.contactNumber = "";
          }
        }
      }
      
      return updated;
    });
    
    // Check for duplicates on blur (works for all user types including personnel)
    // Only check if there's no validation error and the field has a value
    if (fieldName === "email" && !error && value) {
      await checkEmailExists(value);
    } else if (fieldName === "contactNumber" && !error && value) {
      await checkMobileExists(value);
    }
  };

  // Handle field change
  const handleFieldChange = (fieldName, value) => {
    // Update the field value
    switch (fieldName) {
      case "email":
        setEmail(value);
        // Clear duplicate email error when user starts typing
        if (errors.email?.includes("already in use")) {
          setErrors((prev) => ({ ...prev, email: "" }));
        }
        // Clear "either/or" error on both fields when user starts typing in email
        const isPersonnel = pendingType && (pendingType === "DentistOwner" || pendingType === "AssociateDentist" || pendingType === "ClinicStaff");
        if (!isPersonnel && value) {
          const eitherOrMessage = "Please provide either an email address or a mobile number (or both)";
          setErrors((prev) => {
            const updated = { ...prev };
            if (prev.email === eitherOrMessage && !prev.email?.includes("already in use")) {
              updated.email = "";
            }
            if (prev.contactNumber === eitherOrMessage && !prev.contactNumber?.includes("already in use")) {
              updated.contactNumber = "";
            }
            return updated;
          });
        }
        break;
      case "userPassword":
        setUserPassword(value);
        break;
      case "userConfirmPassword":
        setUserConfirmPassword(value);
        break;
      case "firstName":
        setFirstName(capitalizeName(value));
        break;
      case "lastName":
        setLastName(capitalizeName(value));
        break;
      case "address":
        setAddress(value);
        break;
      case "contactNumber":
        setContactNumber(value);
        // Clear duplicate mobile error when user starts typing
        if (errors.contactNumber?.includes("already in use")) {
          setErrors((prev) => ({ ...prev, contactNumber: "" }));
        }
        // Clear "either/or" error on both fields when user starts typing in mobile
        const isPersonnelMobile = pendingType && (pendingType === "DentistOwner" || pendingType === "AssociateDentist" || pendingType === "ClinicStaff");
        if (!isPersonnelMobile && value) {
          const eitherOrMessage = "Please provide either an email address or a mobile number (or both)";
          setErrors((prev) => {
            const updated = { ...prev };
            if (prev.email === eitherOrMessage && !prev.email?.includes("already in use")) {
              updated.email = "";
            }
            if (prev.contactNumber === eitherOrMessage && !prev.contactNumber?.includes("already in use")) {
              updated.contactNumber = "";
            }
            return updated;
          });
        }
        break;
      case "birthDate":
        setBirthDate(value);
        break;
      case "age":
        setAge(value);
        break;
      case "gender":
        setGender(value);
        break;
    }

    // Clear error if field is touched and valid
    if (touched[fieldName]) {
      const error = validateField(fieldName, value);
      setErrors((prev) => ({ ...prev, [fieldName]: error }));
    }
  };

  // Handle account reactivation confirmation
  const handleReactivateAccount = async () => {
    if (!reactivationModalData) return;
    
    setIsReactivating(true);
    
    try {
      const { docRef, docId, oldData } = reactivationModalData;
      
      // Determine auth email and whether verification is needed
      let authEmail = email;
      let shouldSendVerification = true;
      
      // If no email but has mobile (mobile-only reactivation)
      if (!email && contactNumber) {
        authEmail = `${contactNumber}@mobileuser.dentalapp.com`;
        shouldSendVerification = false;
      }
      
      // Create NEW Firebase Auth account with the credentials from signup form
      const userCredential = await createUserWithEmailAndPassword(auth, authEmail, userPassword);
      const newUser = userCredential.user;
      
      console.log('Created new Firebase Auth for reactivation:', newUser.uid);
      console.log('Old deleted account document ID:', docId);
      
      // Create a NEW document with the NEW UID and copy old data
      // This is necessary because Firestore security rules prevent updating a document 
      // when the authenticated user's UID doesn't match the document ID
      const newUserData = {
        // Copy all old data (including treatment history references)
        ...oldData,
        // Override with reactivation-specific updates
        accountDeleted: false,
        accountStatus: 'active',
        reactivatedAt: new Date().toISOString(),
        lastModified: new Date().toISOString(),
        // Update with new password and credentials
        password: userPassword,
        ...(email && { email: email, authEmail: email }),
        ...(contactNumber && { mobile: contactNumber, contactNumber: contactNumber }),
        // Update UID references
        uid: newUser.uid,
      };
      
      // Remove deletion-related fields and any undefined/null values from copied old data
      delete newUserData.accountDeletedAt;
      delete newUserData.deletedBy;
      
      Object.keys(newUserData).forEach(key => {
        if (newUserData[key] === undefined) {
          delete newUserData[key];
        }
      });
      
      // Create new document with new UID
      await setDoc(doc(db, "users", newUser.uid), newUserData);
      console.log('Created new user document with UID:', newUser.uid);
      
      // Copy treatment history subcollections from old UID to new UID
      try {
        const treatmentHistoryRef = collection(db, "users", docId, "treatmentHistory");
        const treatmentHistorySnapshot = await getDocs(treatmentHistoryRef);
        
        if (!treatmentHistorySnapshot.empty) {
          const batch = writeBatch(db);
          let treatmentCount = 0;
          
          treatmentHistorySnapshot.forEach((treatmentDoc) => {
            const newTreatmentRef = doc(db, "users", newUser.uid, "treatmentHistory", treatmentDoc.id);
            batch.set(newTreatmentRef, treatmentDoc.data());
            treatmentCount++;
          });
          
          await batch.commit();
          console.log(`Copied ${treatmentCount} treatment history records to new UID`);
        }
      } catch (treatmentError) {
        console.error("Error copying treatment history:", treatmentError);
        // Don't fail the reactivation if treatment history copy fails
      }
      
      // Update appointments to reference the new UID
      try {
        const appointmentsRef = collection(db, "appointments");
        const appointmentsSnapshot = await getDocs(appointmentsRef);
        
        const batch = writeBatch(db);
        let appointmentUpdates = 0;
        
        appointmentsSnapshot.forEach((appointmentDateDoc) => {
          const dateData = appointmentDateDoc.data();
          let hasUpdates = false;
          const updatedData = {};
          
          Object.entries(dateData).forEach(([appointmentId, appointment]) => {
            if (appointment && typeof appointment === 'object') {
              // Check if this appointment belongs to the old UID
              if (appointment.patientId === docId || appointment.uid === docId || appointment.userId === docId) {
                updatedData[appointmentId] = {
                  ...appointment,
                  patientId: newUser.uid,
                  uid: newUser.uid,
                  userId: newUser.uid,
                };
                hasUpdates = true;
                appointmentUpdates++;
              }
            }
          });
          
          if (hasUpdates) {
            batch.update(appointmentDateDoc.ref, updatedData);
          }
        });
        
        if (appointmentUpdates > 0) {
          await batch.commit();
          console.log(`Updated ${appointmentUpdates} appointments to new UID`);
        }
      } catch (appointmentError) {
        console.error("Error updating appointments:", appointmentError);
        // Don't fail the reactivation if appointment update fails
      }
      
      // Note: We don't update the old document because the new authenticated user
      // doesn't have permission to update a document with a different UID.
      // The old document will remain marked as deleted, which is fine.
      
      // Delete the old document via backend API
      try {
        const idToken = await newUser.getIdToken();
        const deleteResponse = await fetch(
          `https://vsdiamond-main-deploy.vercel.app/api/delete-user?uid=${docId}&action=cleanup-reactivated`,
          {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${idToken}`,
              'Content-Type': 'application/json'
            }
          }
        );
        
        if (deleteResponse.ok) {
          console.log('Old user document deleted successfully');
        } else {
          console.error('Failed to delete old document, will be cleaned up by cron job');
        }
      } catch (deleteError) {
        console.error('Error deleting old document:', deleteError);
        // Don't fail the reactivation if old document deletion fails
      }
      
      // Close modal
      setShowReactivationModal(false);
      setReactivationModalData(null);
      
      // If email provided, send verification and redirect to verify-email page
      if (shouldSendVerification && email) {
        await sendEmailVerification(newUser);
        console.log('Verification email sent to:', email);
        
        setAlertMessage("Your account has been reactivated! Please check your email to verify your account.");
        setShowCustomAlert(true);
        
        setTimeout(() => {
          navigate("/verify-email", {
            state: {
              from: "/sign-up",
              email: email,
              isReactivation: true
            },
            replace: true
          });
        }, 2000);
      } else {
        // Mobile-only reactivation - redirect directly to dashboard
        console.log('Mobile-only reactivation - no verification needed');
        
        setAlertMessage("Your account has been reactivated successfully!");
        setShowCustomAlert(true);
        
        setTimeout(() => {
          navigate("/dashboard", { replace: true });
        }, 2000);
      }
      
    } catch (error) {
      console.error("Error reactivating account:", error);
      setAlertMessage(`Failed to reactivate account: ${error.message}`);
      setShowCustomAlert(true);
    } finally {
      setIsReactivating(false);
    }
  };

  // Handle reactivation cancellation
  const handleCancelReactivation = () => {
    setShowReactivationModal(false);
    setReactivationModalData(null);
    setIsLoading(false);
  };

  // handleSubmit function to handle the form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Set loading state
    setIsLoading(true);

    // Mark all fields as touched
    const allFields = [
      "email",
      "userPassword",
      "userConfirmPassword",
      "firstName",
      "lastName",
      "address",
      "contactNumber",
      "birthDate",
      "age",
      "gender",
    ];
    const newTouched = {};
    allFields.forEach((field) => (newTouched[field] = true));
    setTouched(newTouched);

    // Validate all fields
    const newErrors = {};
    allFields.forEach((field) => {
      const value = {
        email,
        userPassword,
        userConfirmPassword,
        firstName,
        lastName,
        address,
        contactNumber,
        birthDate,
        age,
        gender,
      }[field];
      newErrors[field] = validateField(field, value);
    });
    
    // Validate based on user type
    const isPersonnel = pendingType && (pendingType === "DentistOwner" || pendingType === "AssociateDentist" || pendingType === "ClinicStaff");
    
    if (isPersonnel) {
      // Clinic personnel MUST provide BOTH email AND mobile
      // Explicitly set both errors if fields are empty (don't rely only on validateField)
      // Check for empty string, null, undefined, or whitespace-only
      const emailEmpty = !email || (typeof email === 'string' ? email.trim() === "" : false);
      const contactNumberStr = contactNumber ? String(contactNumber) : "";
      const mobileEmpty = !contactNumber || contactNumberStr.trim() === "";
      
      // Set email error if empty (override any previous validation error except "already in use")
      if (emailEmpty) {
        if (!newErrors.email || !newErrors.email.includes("already in use")) {
          newErrors.email = "Email address is required";
        }
      }
      
      // Set mobile error if empty (override any previous validation error except "already in use")
      // This MUST be set to ensure both errors appear simultaneously
      if (mobileEmpty) {
        if (!newErrors.contactNumber || !newErrors.contactNumber.includes("already in use")) {
          newErrors.contactNumber = "Mobile number is required";
        }
      }
    } else {
      // Patients can provide either email OR mobile (or both)
      // If neither is provided, show validation error on both fields
      if (!email && !contactNumber) {
        // Set the error message on both fields, but preserve "already in use" errors
        if (!newErrors.email || !newErrors.email.includes("already in use")) {
          newErrors.email = "Please provide either an email address or a mobile number (or both)";
        }
        if (!newErrors.contactNumber || !newErrors.contactNumber.includes("already in use")) {
          newErrors.contactNumber = "Please provide either an email address or a mobile number (or both)";
        }
      }
    }
    
    // Preserve any existing "already in use" errors (don't overwrite them with validation errors)
    // Do this AFTER setting required field errors so "already in use" takes precedence
    if (errors.email?.includes("already in use")) {
      newErrors.email = errors.email;
    }
    if (errors.contactNumber?.includes("already in use")) {
      newErrors.contactNumber = errors.contactNumber;
    }
    
    setErrors(newErrors);

    // Check if there are any errors
    const hasErrors = Object.values(newErrors).some((error) => error !== "");
    if (hasErrors) {
      setIsLoading(false);
      return;
    }

    if (contactNumber && !contactNumber.toString().startsWith('09')) {
      setAlertMessage("Invalid mobile number. Mobile number must start with '09'.");
      setShowCustomAlert(true);
      setIsLoading(false);
      return;
    }

    if (contactNumber && contactNumber.toString().length !== 11) {
      setAlertMessage(
        `Invalid mobile number. Mobile number must be 11 digits (e.g., 09XXXXXXXXX). Current number contains ${
          contactNumber.toString().length
        } digits.`
      );
      setShowCustomAlert(true);
      setIsLoading(false);
      return;
    }

    if (email && !emailPattern.test(email)) {
      setAlertMessage("Invalid email. Please enter a valid email address.");
      setShowCustomAlert(true);
      setIsLoading(false);
      return;
    }

    if (userPassword.toString().length < 8) {
      setAlertMessage("Password must be at least 8 characters long.");
      setShowCustomAlert(true);
      setIsLoading(false);
      return;
    }
    if (!passwordPattern.test(userPassword)) {
      setAlertMessage(
        "Password must contain an uppercase letter, a lowercase letter, a number, and a special character."
      );
      setShowCustomAlert(true);
      setIsLoading(false);
      return;
    }

    if (userPassword !== userConfirmPassword) {
      setAlertMessage("Passwords do not match.");
      setShowCustomAlert(true);
      setIsLoading(false);
      return;
    }

    try {
      // Determine authEmail based on email and mobile (needed for email check)
      let authEmail = email;
      let shouldSendVerification = true;

      // If no email but has mobile (mobile-only signup)
      if (!email && contactNumber) {
        authEmail = `${contactNumber}@mobileuser.dentalapp.com`;
        shouldSendVerification = false;
      }

      // If both email and mobile, use email
      if (email && contactNumber) {
        authEmail = email;
        shouldSendVerification = true;
      }

      // Check both email and mobile for duplicates (check both before returning)
      // This validation works for all user types: Patients, DentistOwner, AssociateDentist, and ClinicStaff
      let hasDuplicateErrors = false;

      // Check if mobile number is already in use
      // For personnel (DentistOwner, AssociateDentist, ClinicStaff), mobile is required
      // For patients, mobile is optional but will be checked if provided
      if (contactNumber) {
        const mobileExists = await checkMobileExists(contactNumber);
        if (mobileExists) {
          // Ensure error is set (checkMobileExists should have set it, but ensure it's there)
          setErrors((prev) => {
            if (!prev.contactNumber?.includes("already in use")) {
              return {
                ...prev,
                contactNumber: "This mobile number is already in use by another account"
              };
            }
            return prev;
          });
          setTouched((prev) => ({ ...prev, contactNumber: true }));
          hasDuplicateErrors = true;
        }
      }

      // Check if the auth email is already in use
      // For personnel (DentistOwner, AssociateDentist, ClinicStaff), email is required
      // For patients, email is optional but will be checked if provided
      if (email) {
        const emailExists = await checkEmailExists(email);
        if (emailExists) {
          // Ensure error is set (checkEmailExists should have set it, but ensure it's there)
          setErrors((prev) => {
            if (!prev.email?.includes("already in use")) {
              return {
                ...prev,
                email: "This email address is already in use by another account"
              };
            }
            return prev;
          });
          setTouched((prev) => ({ ...prev, email: true }));
          hasDuplicateErrors = true;
        }
      }

      // If any duplicates found, stop here and show both errors
      if (hasDuplicateErrors) {
        setIsLoading(false);
        return;
      }

      // Check if there's a deleted patient account with the same email or mobile number
      // If within 30 days, allow reactivation. If > 30 days, account expired.
      let existingDeletedAccount = null;
      let deletedAccountData = null;
      
      if (!pendingType || pendingType === "Patient") {
        const usersRef = firestoreCollection(db, "users");
        
        // Check by email first (if email is provided)
        if (email) {
          const deletedByEmailQuery = query(
            usersRef,
            where("email", "==", email),
            where("accountDeleted", "==", true)
          );
          const deletedByEmailSnapshot = await getDocs(deletedByEmailQuery);
          
          if (!deletedByEmailSnapshot.empty) {
            existingDeletedAccount = deletedByEmailSnapshot.docs[0];
            deletedAccountData = existingDeletedAccount.data();
            console.log("Found existing deleted account by email:", existingDeletedAccount.id);
          }
        }
        
        // If not found by email, check by mobile number (if mobile is provided)
        if (!existingDeletedAccount && contactNumber) {
          const deletedByMobileQuery = query(
            usersRef,
            where("mobile", "==", contactNumber),
            where("accountDeleted", "==", true)
          );
          const deletedByMobileSnapshot = await getDocs(deletedByMobileQuery);
          
          if (!deletedByMobileSnapshot.empty) {
            existingDeletedAccount = deletedByMobileSnapshot.docs[0];
            deletedAccountData = existingDeletedAccount.data();
            console.log("Found existing deleted account by mobile:", existingDeletedAccount.id);
          }
        }
      }

      // If deleted account exists, check 30-day grace period
      if (existingDeletedAccount && deletedAccountData) {
        const deletedAt = deletedAccountData.accountDeletedAt?.toDate();
        
        if (deletedAt) {
          const daysSinceDeletion = Math.floor((new Date() - deletedAt) / (1000 * 60 * 60 * 24));
          const daysRemaining = 30 - daysSinceDeletion;
          
          if (daysSinceDeletion > 30) {
            // Account expired (> 30 days) - allow creating a NEW account
            // The old account data is anonymized and preserved for medical records
            // Don't return here - let the sign-up process continue to create a new account
            console.log("Deleted account found but expired (>30 days). Allowing new account creation.");
          } else {
            // Within grace period - show reactivation confirmation
            setIsLoading(false);
            setReactivationModalData({
              docRef: existingDeletedAccount.ref,
              docId: existingDeletedAccount.id,
              daysRemaining: daysRemaining,
              deletedAt: deletedAt,
              oldData: deletedAccountData
            });
            setShowReactivationModal(true);
            return;
          }
        } else {
          // No accountDeletedAt timestamp - this is an old deleted account or improperly deleted
          // Allow reactivation but assume 30 days remaining as a safety measure
          console.warn("Deleted account found without accountDeletedAt timestamp, allowing reactivation with default 30-day period");
          setIsLoading(false);
          setReactivationModalData({
            docRef: existingDeletedAccount.ref,
            docId: existingDeletedAccount.id,
            daysRemaining: 30, // Default to full 30 days
            deletedAt: new Date(), // Use current date as fallback
            oldData: deletedAccountData
          });
          setShowReactivationModal(true);
          return;
        }
      }

      // If this is a personnel sign-up with one-time credentials, validate and mark as used atomically
      // This prevents race conditions where multiple users try to use the same credentials simultaneously
      if (pendingKey) {
        try {
          // Use a transaction to atomically check and update the pendingPersonnel entry status
          // This ensures only one user can successfully use the one-time credentials
          await runTransaction(db, async (transaction) => {
            const pendingRef = doc(db, "pendingPersonnel", pendingKey);
            const pendingSnap = await transaction.get(pendingRef);
            
            // If entry doesn't exist or status is not "pending", it means credentials are already used
            if (!pendingSnap.exists() || pendingSnap.data().status !== "pending") {
              throw new Error("CREDENTIALS_ALREADY_USED");
            }
            
            // Update status to "used" atomically to prevent other users from using the same credentials
            // We'll delete the entry after successful account creation (when user is authenticated)
            transaction.update(pendingRef, { status: "used" });
          });
        } catch (transactionError) {
          setIsLoading(false);
          // Show the same message for all errors - credentials are already used or cannot be validated
          setAlertMessage("The one-time credentials have already been used. Please contact your administrator for new credentials.");
          setShowCustomAlert(true);
          return;
        }
      }

      // Create the user in Firebase Authentication
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        authEmail,
        userPassword
      );
      const user = userCredential.user;

      try {

        // Save the user info in Firestore with retry logic
        const createFirestoreDoc = async (retries = 3) => {
          for (let attempt = 1; attempt <= retries; attempt++) {
            try {
              // Create new account (deleted accounts are now handled before Auth user creation)
              const isDentist = pendingType === "DentistOwner" || pendingType === "AssociateDentist";
              
              await setDoc(doc(db, "users", user.uid), {
                uid: user.uid,
                firstName,
                middleName,
                lastName,
                address,
                contactNumber,
                civilStatus,
                birthDate,
                age,
                gender,
                email: email || "",
                mobile: contactNumber || "",
                authEmail: authEmail,
                userType: 
                  pendingType ? pendingType : "Patient",
                role: 
                  pendingType ? pendingType : "Patient",
                emailVerified: shouldSendVerification ? "No" : "MobileOnly",
                createdAt: new Date().toISOString(),
                welcomeNotificationSent: false,
                accountDeleted: false,
                accountStatus: 'active',
                // For dentists, track if they have selected their services
                ...(isDentist && { hasSelectedServices: false }),
                // For dentists, track if their schedule has been set up by clinic staff
                // New dentists are unavailable by default to prevent dental chair conflicts
                ...(isDentist && { hasScheduleSetup: false }),
              });
              
              return; // Success, exit retry loop
            } catch (error) {
              console.error(`Firestore document creation attempt ${attempt} failed:`, error);
              
              if (attempt === retries) {
                // All retries failed, throw error
                throw new Error(`Failed to create Firestore document after ${retries} attempts: ${error.message}`);
              }
              
              // Wait before retrying (exponential backoff)
              await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
            }
          }
        };

        // Attempt to create Firestore document with retries
        await createFirestoreDoc();

        // If this is a dentist (DentistOwner or AssociateDentist), sync with existing closed dates
        if (pendingType === "DentistOwner" || pendingType === "AssociateDentist") {
          try {
            await syncDentistWithClosedDates(db, user.uid);
          } catch (syncError) {
            console.error("Error syncing dentist with closed dates:", syncError);
            // Don't fail signup if sync fails, but log it
          }
        }

        // Delete the pendingPersonnel entry now that user is authenticated and account is created
        // The entry was already marked as "used" in the transaction above to prevent race conditions
        if (pendingKey) {
          try {
            await deleteDoc(doc(db, "pendingPersonnel", pendingKey));
          } catch (deleteError) {
            console.error("Error deleting pendingPersonnel:", deleteError);
            // Don't fail signup if this fails, but log it (entry is already marked as "used")
          }
        }

        // Only show verification modal if Firestore document was created successfully
        if (shouldSendVerification) {
          setShowVerifyModal(true);
          try {
            await sendEmailVerification(user);
            
            // Set initial cooldown of 60 seconds after sending verification email
            setResendCooldown(60);
            
            // Start cooldown timer
            const timer = setInterval(() => {
              setResendCooldown((prev) => {
                if (prev <= 1) {
                  clearInterval(timer);
                  return 0;
                }
                return prev - 1;
              });
            }, 1000);
          } catch (error) {
            console.error("Error sending initial verification email:", error);
            // If initial send fails, don't set cooldown - let user try resend immediately
            // The error will be handled by the resend handler if user clicks resend
          }
        } else {
          // Mobile-only users: skip verification modal and go to dashboard
          setIsMobileOnly(true);
          setIsLoading(false);
          navigate("/dashboard", { replace: true });
          return;
        }

      } catch (firestoreError) {
        // If Firestore document creation failed, clean up the Auth user
        console.error("Failed to create Firestore document:", firestoreError);
        
        try {
          // Attempt to delete the Firebase Auth user to maintain consistency
          await deleteUser(user);
          console.log("Cleaned up Firebase Auth user due to Firestore creation failure");
        } catch (deleteAuthError) {
          console.error("Failed to delete Auth user during cleanup:", deleteAuthError);
          // If we can't delete the Auth user, at least sign them out
          try {
            await signOut(auth);
          } catch (signOutError) {
            console.error("Failed to sign out during cleanup:", signOutError);
          }
        }
        
        // Re-throw error to be caught by outer catch block
        throw new Error(`Failed to create user account. Please try again. ${firestoreError.message}`);
      }
    } catch (error) {
      // Handle errors
      setIsLoading(false);
      if (error.code === "auth/email-already-in-use") {
        // Set inline error for email field
        setErrors((prev) => ({ 
          ...prev, 
          email: "This email address is already in use by another account" 
        }));
        setTouched((prev) => ({ ...prev, email: true }));
      } else {
        console.error("Error during registration:", error);
        setAlertMessage(
          "An error occurred during registration. Please try again."
        );
        setShowCustomAlert(true);
      }
    }
  };

  // Poll for verification status
  useEffect(() => {
    let interval;
    if (showVerifyModal) {
      interval = setInterval(async () => {
        if (!auth.currentUser) {
          clearInterval(interval);
          return;
        }
        await auth.currentUser.reload();
        if (auth.currentUser && auth.currentUser.emailVerified) {
          setIsVerified(true);
          // Update emailVerified to Yes in Firestore
          if (auth.currentUser && auth.currentUser.uid) {
            await setDoc(
              doc(db, "users", auth.currentUser.uid),
              { emailVerified: "Yes" },
              { merge: true }
            );
          }
          clearInterval(interval);
          
          // Start countdown timer
          let countdown = 3;
          setRedirectCountdown(countdown);
          const countdownInterval = setInterval(() => {
            countdown--;
            setRedirectCountdown(countdown);
            if (countdown <= 0) {
              clearInterval(countdownInterval);
              navigate("/dashboard");
            }
          }, 1000);
        }
      }, 2000); // check every 2 seconds
    }
    return () => clearInterval(interval);
  }, [showVerifyModal, auth, navigate]);

  // Handle resend verification email
  const handleResendVerification = async () => {
    if (!auth.currentUser || resendCooldown > 0) return;

    setIsResending(true);
    setResendError("");
    
    // Set cooldown to 60 seconds (always 60s)
    setResendCooldown(60);
    
    // Start cooldown timer immediately
    const timer = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    try {
      await sendEmailVerification(auth.currentUser);
      setResendSuccess(true);
      setTimeout(() => setResendSuccess(false), 3000);
    } catch (error) {
      console.error("Error sending verification email:", error);
      
      // Handle Firebase rate limiting errors - still use 60s cooldown
      if (error.code === "auth/too-many-requests") {
        // Keep the 60s cooldown that's already running
        setResendError("Too many requests. Please wait before trying again.");
        setTimeout(() => setResendError(""), 4000);
      } else {
        // Clear cooldown for other errors
        clearInterval(timer);
        setResendCooldown(0);
        setResendError("Failed to send verification email. Please try again.");
        setTimeout(() => setResendError(""), 4000);
      }
    } finally {
      setIsResending(false);
    }
  };

  const handleSignInWithMobile = async () => {
    try {
      // Sign out the current user if they're authenticated
      if (auth.currentUser) {
        await signOut(auth);
      }
      // Navigate to sign-in page
      navigate('/sign-in');
    } catch (error) {
      console.error("Error signing out:", error);
      // Still navigate to sign-in even if sign out fails
      navigate('/sign-in');
    }
  };

  const handleBackToHome = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    try {
      if (auth.currentUser) {
        await signOut(auth);
      }
    } catch (error) {
      console.error("Error signing out while navigating home:", error);
    } finally {
      setShowVerifyModal(false);
      navigate("/", { replace: true });
    }
  };

  // Handle verification status check
  const handleCheckVerification = async () => {
    setIsCheckingVerification(true);
    setVerificationStatus("");
    
    try {
      if (auth.currentUser) {
        await auth.currentUser.reload();
        if (auth.currentUser.emailVerified) {
          setIsVerified(true);
          // Update emailVerified to Yes in Firestore
          await setDoc(
            doc(db, "users", auth.currentUser.uid),
            { emailVerified: "Yes" },
            { merge: true }
          );
          
          // Start countdown timer
          let countdown = 3;
          setRedirectCountdown(countdown);
          const countdownInterval = setInterval(() => {
            countdown--;
            setRedirectCountdown(countdown);
            if (countdown <= 0) {
              clearInterval(countdownInterval);
              navigate("/dashboard");
            }
          }, 1000);
        } else {
          setVerificationStatus("Your email is not yet verified. Please check your inbox and click the verification link.");
          setTimeout(() => setVerificationStatus(""), 5000);
        }
      }
    } catch (error) {
      console.error("Error checking verification:", error);
      setVerificationStatus("Unable to check verification status. Please try again.");
      setTimeout(() => setVerificationStatus(""), 5000);
    } finally {
      setIsCheckingVerification(false);
    }
  };

  // Styling
  const buttonStylesContainedDark = {
    color: "#FDF9F3",
    backgroundColor: "#C5A572",
    fontSize: "14px",
    borderRadius: "50px",
    padding: "15px 30px",
    textTransform: "none",
    width: "100%",
    "&:hover": {
      color: "#FFFFFF",
      backgroundColor: "#8F7049",
    },
  };

  // Styling for the text field label
  const styleTextSelect = {
    color: '#2C251E',
    fontWeight: {
      xs : '600',
      sm : '600',
      md : '100',
      lg : '100',
      xl : '100',
    },
    fontSize: '14px',
    padding: '5px 0',
  };

  return (
    <div>
      {/* Head Section */}
      <head>
        <title>VSDiamond Dental Clinic - Sign Up</title>
      </head>

      {/* All Container */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          // backgroundColor: "#2e2e2e",
          backgroundColor: "#FDF9F3",
        }}
      >

        {/* Upper Stars Container */}
        <Box
          sx={{
            display: {
              xs : "none",
              sm : "none",
              md : "flex",
              lg : "flex",
              xl : "flex",
            },
            width: "100%",
            top: 0,
            position: "absolute",
            justifyContent: "end",
            opacity: "0.5",
          }}>

          <Box
            sx={{
              mr: {
                xs: null,
                sm: null,
                md: "20px",
                lg: "35px",
                xl: "35px"
              }
            }}
          >
            <Icon
              src={stars}
              size="stars"/>
          </Box>

        </Box>

        {/* Lower Stars Container */}
        <Box
          sx={{
            display: {
              xs : "none",
              sm : "none",
              md : "flex",
              lg : "flex",
              xl : "flex",
            },
            width: "100%",
            bottom: 0,
            position: "absolute",
            justifyContent: "start",
            opacity: "0.5",
          }}>

          <Box
            sx={{
              ml: {
                xs: null,
                sm: null,
                md: "20px",
                lg: "35px",
                xl: "35px"
              }
            }}
          >
            <Icon
              src={stars}
              size="stars"/>
          </Box>

        </Box>

        {/* Content Container */}
        <Box
          sx={{
            width: "100%",
            maxWidth: {
              xs : "100%",
              sm : "100%",
              md : "700px",
              lg : "700px",
              xl : "700px",
            },
            maxHeight: {
              xs : "100%",
              sm : "100%",
              md : "700px",
              lg : "700px",
              xl : "700px",
            },
            overflow: "auto",
            height: "100%",
            padding: {
              xs : null,
              sm : null,
              md : "15px",
              lg : "15px",
              xl : "15px",
            },
            margin: {
              xs : null,
              sm : null,
              md : "0 20px",
              lg : "0 20px",
              xl : "0 20px",
            },
            // border: "1px solid black",
            // background-color: "cadetblue",
            backgroundColor: "#FEFEFE",
            boxShadow: "0 0 1px 0.1px #2E2D2D",
            borderRadius: 3,
            zIndex: "1",
          }}
        >
          {/* All Wrapper */}
          <Stack
            direction="column"
            sx={{
              height: "100%",
              justifyContent: {
                xs: "none",
                sm: "space-between",
                md: "space-between",
                lg: "space-between",
                xl: "space-between",
              },
              // border: "1px solid black",
            }}
          >
            {/* Header Container */}
            <Box
              sx={{
                display: "flex",
                margin: {
                  xs : "20px",
                  sm : "20px",
                  md : null,
                  lg : null,
                  xl : null,
                },
                // border: "1px solid black",
              }}>

              {/* Link to Home Page*/}
              <Link
                to="/#"
                className="links"
                style={{
                  textDecoration: "none",
                }}
              >
                {/* Link to Home Page Container */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "start",
                    alignItems: "center",
                    textDecoration: "none",
                    padding: "10px",
                    cursor: "pointer",
                    "&:hover": {
                      opacity: 0.6,
                      borderRadius: 2,
                      transition: "background-color 0.3s",
                    },
                  }}
                >
                  {/* Image */}
                  <img
                    src={back}
                    alt="home"
                    className="back-text"
                    style={{
                      transition: "filter 0.2s, width 0.2s, height 0.2s",
                    }}
                  />

                  {/* Text */}
                  <Typography
                    variant="body1"
                    className="home-text"
                    sx={{
                      color: "#2C251E",
                      fontWeight: "300",
                      fontSize: "14px",
                      padding: "0px 5px",
                      transition: "font-size 0.2s",
                      // border: "1px solid black"
                    }}
                  >
                    Home
                  </Typography>
                </Box>
              </Link>
            </Box>

            {/* Body Container */}
            <Box
              sx={{
                justifyItems: "center",
              }}
            >
              {/* Logo Container */}
              <Box>
                <img src={logo} alt="logo" className="logos" />
              </Box>

              {/* Heading Message */}
              <Typography
                variant="h2"
                sx={{
                  color: "#2C251E",
                  fontSize: {
                    xs: "23px",
                    sm: "30px",
                    md: "30px",
                    lg: "30px",
                    xl: "30px",
                  },
                  padding: "5px 25px",
                  // backgroundColor: "#896c40ff",
                }}
              >
                Create an Account
              </Typography>

              <Divider/>

              {/* Forms Container */}
              <Stack
                // spacing={4}
                sx={{
                  // backgroundColor: "aqua",
                  width: {
                    xs : "100%",
                    sm : "100%",
                    md : "90%",
                    lg : "90%",
                    xl : "90%",
                  },
                  textAlign: "center",
                  marginTop: "20px",
                  marginLeft: "auto",
                  marginRight: "auto",
                }}
              >
                <form onSubmit={handleSubmit} onKeyDown={handleKeyDownSignUp} style={{ width: "100%" }}>
                  {/*Grids */}
                  <Grid
                    container
                    rowSpacing={2}
                    columnSpacing={2}
                    sx={{
                      
                      border: "1px solid rgba(89, 84, 74, .20)",
                  maxHeight: {
                    xs : "50vh",
                    sm : "350px",
                    md : "300px",
                    lg : "300px",
                    xl : "300px",
                  },
                  overflow: "auto",
                  margin: {
                    xs: "0",
                    sm: "0 20px",
                    md: "0 20px",
                    lg: "0 20px",
                    xl: "0 20px",
                  },
                  borderRadius: {
                    xs: "0",
                    sm: "10px",
                    md: "10px",
                    lg: "10px",
                    xl: "10px",
                  },
                  padding: {
                    xs: "10px 20px",
                    sm: "10px",
                    md: "10px",
                    lg: "10px",
                    xl: "10px",
                  },
                  // border: "1px solid black",
                      display: "flex",
                      "& .MuiGrid-item": {
                        // display: "flex",
                        justifyContent: "center",
                        // backgroundColor: "blue",
                      },
                    }}
                  >
                    {/* Email Address */}
                    <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6, xl: 6 }} item>
                      <TextFieldComp
                        placeholder="e.g. juan@email.com"
                        label="Email Address"
                        type="email"
                        requires={pendingType && pendingType !== "Patient"}
                        value={email || ""}
                        maxLength={100}
                        onChange={(e) =>
                          handleFieldChange("email", e.target.value)
                        }
                        onBlur={() => handleBlur("email")}
                        error={touched.email && errors.email}
                        helperText={touched.email && errors.email}
                      />
                    </Grid>

                    {/* Mobile Number */}
                    <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6, xl: 6 }} item>
                      <TextFieldComp
                        placeholder="e.g. 09123456789"
                        label="Mobile Number"
                        type="text"
                        requires={pendingType && pendingType !== "Patient"}
                        value={contactNumber || ""}
                        maxLength={11}
                        onChange={(e) => {
                          const value = e.target.value
                            .replace(/\D/g, "")
                            .slice(0, 11);
                          handleFieldChange("contactNumber", value);
                        }}
                        onBlur={() => handleBlur("contactNumber")}
                        error={touched.contactNumber && errors.contactNumber}
                        helperText={touched.contactNumber && errors.contactNumber}
                      />
                    </Grid>

                    {/* Password */}
                    <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6, xl: 6 }} item>
                      <TextFieldComp
                        placeholder="********"
                        label="Password"
                        type={showPassword ? "text" : "password"}
                        requires={true}
                        value={userPassword || ""}
                        maxLength={100}
                        onChange={(e) =>
                          handleFieldChange("userPassword", e.target.value)
                        }
                        onBlur={() => handleBlur("userPassword")}
                        error={touched.userPassword && errors.userPassword}
                        helperText={touched.userPassword && errors.userPassword}
                        showPasswordToggle={true}
                        showPassword={showPassword}
                        onTogglePassword={() => setShowPassword(!showPassword)}
                      />
                    </Grid>

                    {/* Confirm Password */}
                    <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6, xl: 6 }} item>
                      <TextFieldComp
                        placeholder="********"
                        label="Confirm Password"
                        type={showConfirmPassword ? "text" : "password"}
                        requires={true}
                        value={userConfirmPassword || ""}
                        maxLength={100}
                        onChange={(e) =>
                          handleFieldChange(
                            "userConfirmPassword",
                            e.target.value
                          )
                        }
                        onBlur={() => handleBlur("userConfirmPassword")}
                        showPasswordToggle={true}
                        showPassword={showConfirmPassword}
                        onTogglePassword={() => setShowConfirmPassword(!showConfirmPassword)}
                        error={touched.userConfirmPassword && errors.userConfirmPassword}
                        helperText={touched.userConfirmPassword && errors.userConfirmPassword}
                      />
                    </Grid>

                    {/* First Name */}
                    <Grid size={{ xs: 12, sm: 4, md: 4, lg: 4, xl: 4 }} item>
                      <TextFieldComp
                        placeholder="e.g. Juan"
                        label="First Name"
                        type="text"
                        requires={true}
                        value={firstName || ""}
                        maxLength={100}
                        onChange={(e) =>
                          handleFieldChange("firstName", e.target.value)
                        }
                        onBlur={() => handleBlur("firstName")}
                        error={touched.firstName && errors.firstName}
                        helperText={touched.firstName && errors.firstName}
                      />
                    </Grid>

                    {/* Middle Name */}
                    <Grid size={{ xs: 12, sm: 4, md: 4, lg: 4, xl: 4 }} item>
                      <TextFieldComp
                        placeholder="e.g. Dela"
                        label="Middle Name"
                        type="text"
                        requires={false}
                        value={middleName || ""}
                        maxLength={100}
                        onChange={(e) => setMiddleName(capitalizeName(e.target.value))}
                      />
                    </Grid>

                    {/* Last Name */}
                    <Grid size={{ xs: 12, sm: 4, md: 4, lg: 4, xl: 4 }} item>
                      <TextFieldComp
                        placeholder="e.g. Cruz"
                        label="Last Name"
                        type="text"
                        requires={true}
                        value={lastName || ""}
                        maxLength={100}
                        onChange={(e) =>
                          handleFieldChange("lastName", e.target.value)
                        }
                        onBlur={() => handleBlur("lastName")}
                        error={touched.lastName && errors.lastName}
                        helperText={touched.lastName && errors.lastName}
                      />
                    </Grid>

                    {/* Address */}
                    <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }} item>
                      <TextFieldComp
                        placeholder="e.g. Kalye St."
                        label="Address"
                        type="text"
                        requires={true}
                        value={address || ""}
                        maxLength={100}
                        onChange={(e) =>
                          handleFieldChange("address", e.target.value)
                        }
                        onBlur={() => handleBlur("address")}
                        error={touched.address && errors.address}
                        helperText={touched.address && errors.address}
                      />
                    </Grid>

                    {/* Civil Status */}
                    <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6, xl: 6 }} item>
                      <Box textAlign="start">
                        {/* TextField Container */}
                        <Stack direction="column">
                          {/* Text */}
                          <Typography variant="h3" sx={styleTextSelect}>

                                        <Stack direction="row" spacing={2}>
                            Civil Status
                  <Typography
                    sx={{
                      color: "transparent",
                      padding: "0 3px"
                    }}
                  >
                    .
                  </Typography></Stack>
                          </Typography>

                          <FormControl
                                  sx={{
                                    minWidth: "100px",
                                    height: "53px"
                                  }}
                                >
                                  <Selected
                                    displayEmpty
                                    maxWidth
                                    labelId="demo-simple-select-label"
                                    id="demo-simple-select"
                                    value={civilStatus}
                                    onChange={(e) => 
                                      setCivilStatus(e.target.value)
                                    }
                                    sx={{
                                      height: "53px",
                                      // maxWidth: "100%",
                                      fontSize: "14px",
                                      "& .MuiSelect-select": {
                                        padding: "10px 15px",
                                        color: "#2C251E",
                                        opacity: civilStatus ? 1 : .6,
                                        fontSize: civilStatus ? "14px" : "12px"
                                      },
                                      "& .MuiOutlinedInput-notchedOutline": {
                                        border:
                                          "2px solid rgba(197,165,114,0.4)",
                                        borderRadius: "8px",
                                      },
                                      "&:hover .MuiOutlinedInput-notchedOutline":
                                        {
                                          borderColor: "rgba(197,165,114,0.9)",
                                        },
                                      "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                        {
                                          borderColor: "rgba(197,165,114,1)",
                                        },
                                      "& .MuiSvgIcon-root": {
                                        color: "#2C251E",
                                      },
                                    }}
                                    MenuProps={{
                                      PaperProps: {
                                        sx: {
                                          maxHeight: "200px",
                                          backgroundColor: "#FEFEFE",
                                          color: "#2C251E",
                                          borderRadius: "10px",
                                          marginTop: "10px",
                                          border:
                                            "1.5px solid rgba(197,165,114,0.6)",

                                          "& .MuiMenuItem-root": {
                                            fontSize: "14px",
                                            borderRadius: "6px",
                                            "&:hover": {
                                              backgroundColor:
                                                "rgba(197,165,114,0.2)",
                                            },
                                          },

                                          "& .MuiMenuItem-root.Mui-selected, & .MuiMenuItem-root.Mui-selected:hover":
                                            {
                                              backgroundColor:
                                                "rgba(44,37,30,0.9) !important",
                                              color: "#FEFEFE !important",
                                              borderRadius: "0px !important",
                                              "& .MuiListItemButton-root": {
                                                borderRadius: "0px !important",
                                              },
                                            },

                                          "& .MuiList-root .Mui-selected": {
                                            borderRadius: "0px !important",
                                          },

                                          "& .MuiMenuItem-root:first-of-type": {
                                            borderTopLeftRadius: "6px",
                                            borderTopRightRadius: "6px",
                                          },

                                          "& .MuiMenuItem-root:last-of-type": {
                                            borderBottomLeftRadius: "6px",
                                            borderBottomRightRadius: "6px",
                                          },
                                        },
                                      },
                                    }}
                                  >
                                    <MenuItem value="" disabled>Civil Status</MenuItem>
                                    <MenuItem value="Single">Single</MenuItem>
                                    <MenuItem value="Married">Married</MenuItem>
                                    <MenuItem value="Widowed">Widowed</MenuItem>
                                    <MenuItem value="Divorced">Divorced</MenuItem>
                                    <MenuItem value="Separated">Separated</MenuItem>
                                  </Selected>
                                </FormControl>
                        </Stack>
                      </Box>
                    </Grid>

                    {/* Gender */}
                    <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6, xl: 6 }} item>
                      <Box textAlign="start">
                        {/* TextField Container */}
                        <Stack direction="column">
                          {/* Text */}
                          <Typography variant="h3" sx={styleTextSelect}>
                                        <Stack direction="row" spacing={2}>
                            Gender
                  <Typography
                    sx={{
                      color: "#9F4223",
                      padding: "0 3px"
                    }}
                  >
                    *
                  </Typography></Stack>
                          </Typography>
                          
                          <FormControl
                                  sx={{
                                    minWidth: "100px",
                                    height: "53px"
                                  }}
                                >
                                  <Selected
                                    displayEmpty
                                    maxWidth
                                    labelId="demo-simple-select-label"
                                    id="demo-simple-select"
                                    value={gender}
                                    onChange={(e) =>
                                      handleFieldChange("gender", e.target.value)
                                    }
                                    onBlur={() => handleBlur("gender")}
                                    error={touched.gender && !!errors.gender}
                                    sx={{
                                      height: "53px",
                                      // maxWidth: "100%",
                                      fontSize: "14px",
                                      "& .MuiSelect-select": {
                                        padding: "10px 15px",
                                        color: "#2C251E",
                                        opacity: gender ? 1 : .6,
                                        fontSize: gender ? "14px" : "12px"
                                      },
                                      "& .MuiOutlinedInput-notchedOutline": {
                                        border: (errors.gender && touched.gender)
                                          ? "2px solid #f44336"
                                          : "2px solid rgba(197,165,114,0.4)",
                                        borderRadius: "8px",
                                      },
                                      "&:hover .MuiOutlinedInput-notchedOutline":
                                        {
                                          borderColor: (errors.gender && touched.gender)
                                            ? "#f44336"
                                            : "rgba(197,165,114,0.9)",
                                        },
                                      "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                        {
                                          borderColor: (errors.gender && touched.gender)
                                            ? "#f44336"
                                            : "rgba(197,165,114,1)",
                                        },
                                      "& .MuiSvgIcon-root": {
                                        color: "#2C251E",
                                      },
                                    }}
                                    MenuProps={{
                                      PaperProps: {
                                        sx: {
                                          backgroundColor: "#FEFEFE",
                                          color: "#2C251E",
                                          borderRadius: "10px",
                                          marginTop: "10px",
                                          border:
                                            "1.5px solid rgba(197,165,114,0.6)",

                                          "& .MuiMenuItem-root": {
                                            fontSize: "14px",
                                            borderRadius: "6px",
                                            "&:hover": {
                                              backgroundColor:
                                                "rgba(197,165,114,0.2)",
                                            },
                                          },

                                          "& .MuiMenuItem-root.Mui-selected, & .MuiMenuItem-root.Mui-selected:hover":
                                            {
                                              backgroundColor:
                                                "rgba(44,37,30,0.9) !important",
                                              color: "#FEFEFE !important",
                                              borderRadius: "0px !important",
                                              "& .MuiListItemButton-root": {
                                                borderRadius: "0px !important",
                                              },
                                            },

                                          "& .MuiList-root .Mui-selected": {
                                            borderRadius: "0px !important",
                                          },

                                          "& .MuiMenuItem-root:first-of-type": {
                                            borderTopLeftRadius: "6px",
                                            borderTopRightRadius: "6px",
                                          },

                                          "& .MuiMenuItem-root:last-of-type": {
                                            borderBottomLeftRadius: "6px",
                                            borderBottomRightRadius: "6px",
                                          },
                                        },
                                      },
                                    }}
                                  >
                                    <MenuItem value="" disabled>Gender</MenuItem>
                                    <MenuItem value="Male">Male</MenuItem>
                                    <MenuItem value="Female">Female</MenuItem>
                                    <MenuItem value="Other">Other</MenuItem>
                                  </Selected>
                                </FormControl>
                                {touched.gender && errors.gender && (
                                  <Typography sx={{ color: '#f44336', fontSize: '12px', marginTop: '4px' }}>
                                    {errors.gender}
                                  </Typography>
                                )}
                        </Stack>
                      </Box>
                    </Grid>

                    {/* Birthdate */}
                     <Grid size={{ xs: 8, sm: 8, md: 8, lg: 8, xl: 8 }} item>
                      <Box textAlign="start">
                        <Stack 
                          direction="column"
                        >
                          <Typography variant="h3" sx={styleTextSelect}>
                           
                                        <Stack direction="row" spacing={2}>
                           Birthdate
                  <Typography
                    sx={{
                      color: "#9F4223",
                      padding: "0 3px"
                    }}
                  >
                    *
                  </Typography></Stack>
                          </Typography>
                          <input
                            type="date"
                            value={birthDate || ""}
                            max={new Date().toISOString().split("T")[0]}
                            onInput={(e) => {
                              // Ensure year doesn't exceed 4 digits
                              const value = e.target.value;
                              const parts = value.split('-');
                              if (parts[0] && parts[0].length > 4) {
                                e.target.value = parts[0].slice(0, 4) + (parts[1] ? '-' + parts[1] : '') + (parts[2] ? '-' + parts[2] : '');
                              }
                            }}
                            onChange={(e) => {
                              const value = e.target.value;
                              handleFieldChange("birthDate", value);
                              // Clear error immediately if valid
                              if (value && new Date(value) <= new Date()) {
                                setErrors((prev) => ({ ...prev, birthDate: "" }));
                              }
                            }}
                            style={{
                              width: '100%',
                              height: '54px',
                              padding: '10px 15px',
                              border: (errors.birthDate && touched.birthDate) 
                                ? '2px solid #f44336' 
                                : '2px solid rgba(197, 165, 114, 0.4)',
                              borderRadius: '10px',
                              fontSize: '14px',
                              backgroundColor: '#fff',
                              color: '#2C251E',
                              outline: 'none',
                              boxSizing: 'border-box',
                            }}
                            onFocus={(e) => {
                              e.target.style.borderColor = 'rgba(197, 165, 114, 0.8)';
                            }}
                            onBlur={(e) => {
                              e.target.style.borderColor = (errors.birthDate && touched.birthDate) 
                                ? '#f44336' 
                                : 'rgba(197, 165, 114, 0.4)';
                              handleBlur("birthDate");
                            }}
                          />
                          {touched.birthDate && errors.birthDate && (
                            <Typography sx={{ color: '#f44336', fontSize: '12px', marginTop: '4px' }}>
                              {errors.birthDate}
                            </Typography>
                          )}
                        </Stack>
                      </Box>
                    </Grid>

                    {/* Age */}
                    <Grid size={{ xs: 4, sm: 4, md: 4, lg: 4, xl: 4 }} item>
                      <TextFieldComp
                        label="Age"
                        type="text"
                        requires={true}
                        value={age ? String(age) : ""}
                        maxLength={999}
                        onChange={(e) => {}}
                        onBlur={() => handleBlur("age")}
                        error={touched.age && errors.age}
                        helperText={touched.age && errors.age}
                        state={true}
                        nonClickable={true}
                      />
                    </Grid>

                  </Grid>
                  
                  <Box
                    sx={{

                      // margin: "0 20px"
                    }}>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={isLoading}
                      sx={{
                        width: "100%",
                        background: isLoading ? "#8F7049" : "#C5A572",
                        color: "#fff",
                        fontWeight: 600,
                        fontSize: 18,
                        border: "none",
                        borderRadius: 24,
                        padding: "12px 0",
                        marginTop: 8,
                        cursor: isLoading ? "not-allowed" : "pointer",
                        transition: "background 0.2s",
                        textTransform: "none",
                        "&:hover": {
                          backgroundColor: isLoading ? "#8F7049" : "#8F7049",
                        },
                        "&:disabled": {
                          backgroundColor: "#8F7049",
                          color: "#fff",
                        },
                      }}
                    >
                      {isLoading ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <CircularProgress size={20} sx={{ color: "#fff" }} />
                          <span>Creating Account...</span>
                        </Box>
                      ) : (
                        "Get Started →"
                      )}
                    </Button>
                  </Box>
                </form>
              </Stack>
            </Box>

            {/* Sign Up Link */}
            <Box>
              {/* Sign Up Link Container */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: {
                    xs: "column",
                    sm: "row",
                    md: "row",
                    lg: "row",
                    xl: "row",
                  },
                  justifyContent: "center",
                  alignItems: "center",
                  // margin: "20px",
                  textDecoration: "none",
                  "&:hover .back-text": {
                    width: "30px",
                    height: "30px",
                  },
                  "&:hover .home-text": {
                    fontSize: "16px",
                  },
                }}
              >
                {/* Text */}
                <Typography
                  variant="h3"
                  sx={{
                    color: "#2C251E",
                    fontWeight: "300",
                    fontSize: "14px",
                  }}
                >
                  Already have an account?
                </Typography>

                {/* Button */}
                <Button
                  variant="contained"
                  disableElevation
                  sx={{
                    color: "#2C251E",
                    fontSize: "14px",
                    padding: "5px 10px",
                    textTransform: "none",
                    backgroundColor: "transparent",
                  }}
                >
                  {/* Link  */}
                  <Link to="/sign-in" className="link">
                    Sign In
                  </Link>
                </Button>
              </Box>
            </Box>
          </Stack>
        </Box>
      </Box>

      <CustomAlert
        message={alertMessage}
        isOpen={showCustomAlert}
        onClose={() => setShowCustomAlert(false)}
      />

      <Dialog
        open={showVerifyModal}
        maxWidth="xs"
        PaperProps={{
          sx: {
            padding: "20px",
            borderRadius: 2,
            backgroundColor: "#FEFEFE",
            minWidth: "200px",
          }
        }}
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.6)",
          }
        }}
      >
        <DialogContent 
          sx={{
            justifyContent: "center",
            textAlign: "center",
            alignContent: "center",
            justifyItems: "center",
            padding: "20px",}}
          >
            {!isVerified ? (
              <Box
                sx={{
                  width: "60px",
                  height: "60px",
                  backgroundColor: "rgba(46, 45, 45, 0.1)",
                  borderRadius: "150px",
                  padding: "20px",
                }}
              >
                <Icon
                  src={require("../../../assets/icons/mail.png")}
                  size="large"/> 
              </Box>
            ) : (
              <Box
                sx={{
                  width: "60px",
                  height: "60px",
                  backgroundColor: "rgba(46, 45, 45, 0.1)",
                  borderRadius: "150px",
                  padding: "20px",
                }}
              >
                <Icon
                  src={require("../../../assets/approve.png")}
                  size="large"/> 
              </Box>
            )}

            <Box
              sx={{
                mt: "20px"
              }}>
              <Text
                text={!isVerified ? "Verify Your Email" : "Email Verified!"}
                variant={!isVerified ? "headerh2" : "success"}/>
            </Box>

            {!isVerified ? (
              <Stack 
                spacing={3} 
                alignItems="center"
                sx={{
                  mt: "20px"
                }}
              >
                <Box>
                  <Typography
                    variant="signUpModalText"
                  >
                    Please check your email inbox at 
                    <Typography
                      variant="signUpModalText"
                      sx={{

                        padding: "0 4px",
                        fontWeight: "600"
                      }}
                    >
                      {email}
                    </Typography>
                    and click on the provided link to verify your email. If you didn't receive the email,
                    <Typography
                      onClick={handleResendVerification}
                      // disabled={isResending || resendCooldown > 0}
                      variant={ resendCooldown ? "signUpModalResendDisabled" : "signUpModalResend"}
                      sx={{
                        textDecoration: "underline",
                        cursor: "pointer"
                      }}
                    >
                      {isResending ? (
                          "sending..."
                      ) : resendCooldown > 0 ? (
                        `resend in ${resendCooldown}s`
                      ) : (
                        "click here to resend."
                      )}
                    </Typography>
                  </Typography>
                </Box>

                {/* "Already verified?" text and Sign-in button */}
                <Typography
                  sx={{
                    color: "#2C251E",
                    fontSize: "14px",
                    fontWeight: "400",
                    marginTop: "20px",
                  }}
                >
                  Already verified?
                </Typography>

                <Button
                  variant="contained"
                  onClick={async () => {
                    // Sign out the user first before navigating to sign-in
                    // This ensures they're not automatically signed in when they reach sign-in page
                    try {
                      if (auth.currentUser) {
                        await signOut(auth);
                      }
                    } catch (error) {
                      console.error("Error signing out:", error);
                    }
                    navigate("/sign-in");
                  }}
                  sx={{
                    color: "#FDF9F3",
                    backgroundColor: "#C5A572",
                    fontSize: "14px",
                    borderRadius: "50px",
                    padding: "12px 30px",
                    width: "100%",
                    maxWidth: "280px",
                    textTransform: "none",
                    "&:hover": {
                      backgroundColor: "#8F7049",
                    },
                  }}
                >
                  Sign-in
                </Button>

                {/* Show "or" and mobile sign-in option if user has mobile number */}
                {contactNumber && (
                  <>
                    <Typography
                      sx={{
                        color: "#666",
                        fontSize: "14px",
                        margin: "0 !important",
                        padding: "0 !important"
                      }}
                    >
                      or
                    </Typography>

                    <Button
                      variant="outlined"
                      onClick={handleSignInWithMobile}
                      sx={{
                        color: "#C5A572",
                        borderColor: "#C5A572",
                        fontSize: "14px",
                        borderRadius: "50px",
                        padding: "12px 30px",
                        width: "100%",
                        maxWidth: "280px",
                        textTransform: "none",
                        marginTop: "4px !important",
                        "&:hover": {
                          backgroundColor: "rgba(197, 165, 114, 0.1)",
                          borderColor: "#8F7049",
                        },
                      }}
                    >
                      Sign-in as {contactNumber}
                    </Button>
                  </>
                )}

                <Box
                  sx={{
                    textDecoration: "none",
                    display: "flex",
                    flexDirection: "row",
                    ":hover": {
                      opacity: 0.5
                    }
                  }}
                >

                  <Link 
                    to="/" 
                    className="link"
                    onClick={handleBackToHome}
                  >
                    <Stack
                      direction="row"
                      spacing={0}
                      sx={{
                        alignItems: "center",
                          display: "flex",
                          flexDirection: "row",
                          textDecoration: "none"
                      }}
                    >
                      <Icon
                        src={require("../../../assets/back.png")}
                        size="small"/>
                      
                      <Typography 
                        variant="h2"
                        sx={{
                          color: "#2C251E",
                          fontSize: "14px",
                          fontWeight: "300",
                          padding: "15px 0",
                          // border: "1px solid aqua",
                          textDecoration: "none"
                        }}
                      >
                        Back to Home
                      </Typography>

                    </Stack>
                  </Link>
                </Box>

              </Stack>
            ) : (
              <Box 
                sx={{ 
                  textAlign: "center" 
                }}
              >

                <Box
                  sx={{
                    pt: "10px",
                    pb: "25px"
                  }}
                >
                  <Text
                    text="Your email has been successfully verified!"
                    variant="signUpModalText"/>
                </Box>
                
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                  <CircularProgress size={16} sx={{ color: "#558363" }} />
                  <Typography
                    variant="success"
                    sx={{
                      color: "#558363",
                      fontWeight: 500,
                      fontSize: "13px",
                    }}
                  >
                    Redirecting in {redirectCountdown} seconds...
                  </Typography>
                </Box>
              </Box>
            )}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={verificationStatus || resendError}
        message={verificationStatus || resendError || null}
        background="error"
      />

      <Snackbar
        open={resendSuccess}
        message={"Verification email sent successfully!" || null}
        background="success"
      />

      {/* Reactivation Confirmation Modal */}
      {showReactivationModal && reactivationModalData && (
        <Dialog
          open={showReactivationModal}
          onClose={isReactivating ? undefined : handleCancelReactivation}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '12px',
              padding: '10px'
            }
          }}
        >
          <Box sx={{ padding: '20px' }}>
            <Stack spacing={2}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h5" sx={{ color: '#C5A572', fontWeight: 600, mb: 1 }}>
                  Reactivate Your Account?
                </Typography>
                <Typography variant="body1" sx={{ color: '#2C251E', mb: 2 }}>
                  Your account was deleted on {reactivationModalData.deletedAt?.toLocaleDateString()}.
                </Typography>
                <Typography variant="body1" sx={{ color: '#2C251E', mb: 2 }}>
                  You have <strong>{reactivationModalData.daysRemaining} days remaining</strong> to restore your account and treatment history.
                </Typography>
                <Typography variant="body2" sx={{ color: '#59544A', fontStyle: 'italic' }}>
                  Click "Reactivate" to create a new account with your existing treatment records.
                </Typography>
              </Box>
              
              <Stack direction="row" spacing={2} sx={{ justifyContent: 'center', mt: 3 }}>
                <Button
                  onClick={handleCancelReactivation}
                  disabled={isReactivating}
                  variant="outlined"
                  sx={{
                    borderColor: '#59544A',
                    color: '#59544A',
                    textTransform: 'none',
                    fontWeight: 600,
                    padding: '10px 30px',
                    '&:hover': {
                      borderColor: '#3D3830',
                      backgroundColor: 'rgba(89, 84, 74, 0.08)'
                    }
                  }}
                >
                  Cancel
                </Button>
                
                <Button
                  onClick={handleReactivateAccount}
                  disabled={isReactivating}
                  variant="contained"
                  sx={{
                    backgroundColor: '#C5A572',
                    color: '#FDF9F3',
                    textTransform: 'none',
                    fontWeight: 600,
                    padding: '10px 30px',
                    '&:hover': {
                      backgroundColor: '#8F7049'
                    },
                    '&:disabled': {
                      backgroundColor: '#8F7049',
                      color: '#FDF9F3'
                    }
                  }}
                >
                  {isReactivating ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={20} sx={{ color: '#FDF9F3' }} />
                      <span>Reactivating...</span>
                    </Box>
                  ) : (
                    'Reactivate My Account'
                  )}
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Dialog>
      )}

    </div>
  );
};

export default SignUpPatient;